// The shared frame around every arcade game.
//
// It owns everything the games have in common -- paying the token, the score
// readout, game-over, replay -- so each game only has to be a game. A game
// receives `onGameOver(score)` and calls it once; the shell does the rest.

import { type ReactNode, useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { useArcade, useRecordScore, useStartPlay } from '../../api/hooks'
import type { ArcadeScoreResult } from '../../api/types'
import { Button, Card, ErrorText, Spinner } from '../ui'
import { sfx } from '../../lib/sound'

export interface GameProps {
  /** Call exactly once, when the run ends. */
  onGameOver: (score: number) => void
  /** Live score, so the shell can show it in the header. */
  onScore?: (score: number) => void
}

interface GameShellProps {
  game: string
  title: string
  instructions: ReactNode
  children: (props: GameProps & { key: number }) => ReactNode
  /** Lower is better (times, moves) rather than higher. */
  lowerIsBetter?: boolean
}

type Stage = 'ready' | 'playing' | 'over'

export default function GameShell({
  game,
  title,
  instructions,
  children,
  lowerIsBetter = false,
}: GameShellProps) {
  const { data: arcade, isPending } = useArcade()
  const startPlay = useStartPlay()
  const recordScore = useRecordScore()

  const [stage, setStage] = useState<Stage>('ready')
  const [score, setScore] = useState(0)
  const [result, setResult] = useState<ArcadeScoreResult | null>(null)
  // Bumped on every replay to force a fresh game instance rather than trying
  // to reset one; a half-reset game is where arcade bugs come from.
  const [round, setRound] = useState(0)

  const entry = arcade?.games.find((g) => g.game === game)

  const begin = useCallback(() => {
    startPlay.mutate(game, {
      onSuccess: () => {
        setScore(0)
        setResult(null)
        setRound((r) => r + 1)
        setStage('playing')
      },
    })
  }, [game, startPlay])

  const finish = useCallback(
    (finalScore: number) => {
      setScore(finalScore)
      setStage('over')
      sfx.thud()
      recordScore.mutate(
        { game, score: finalScore },
        { onSuccess: (data) => setResult(data) },
      )
    },
    [game, recordScore],
  )

  if (isPending) return <Spinner />

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <div className="flex items-center justify-between text-sm">
        <Link to="/arcade" className="text-muted hover:text-ink">
          ← Arcade
        </Link>
        <div className="flex items-center gap-4">
          <span className="text-muted">
            Best {entry?.best_score ?? 0}
          </span>
          {stage === 'playing' && (
            <span className="font-semibold tabular-nums">{score}</span>
          )}
          <span className="text-muted">🪙 {arcade?.balance ?? 0}</span>
        </div>
      </div>

      <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>

      {stage === 'ready' && (
        <Card className="space-y-4">
          <div className="text-sm text-muted">{instructions}</div>
          <ErrorText error={startPlay.error} />
          <Button
            onClick={begin}
            disabled={startPlay.isPending}
            className="w-full"
          >
            {entry?.free_play_available
              ? 'Play — first go is free'
              : `Play — ${arcade?.play_cost ?? 10} tokens`}
          </Button>
          {!entry?.free_play_available &&
            (arcade?.balance ?? 0) < (arcade?.play_cost ?? 10) && (
              <p className="text-center text-sm text-muted">
                Not enough tokens. Add words or finish a lesson to earn more.
              </p>
            )}
        </Card>
      )}

      {stage === 'playing' && (
        <div key={round}>
          {children({ key: round, onGameOver: finish, onScore: setScore })}
        </div>
      )}

      {stage === 'over' && (
        <Card className="space-y-4 text-center">
          <p className="text-sm uppercase tracking-wide text-muted">
            {lowerIsBetter ? 'Finished in' : 'Score'}
          </p>
          <p className="text-5xl font-semibold tabular-nums">{score}</p>
          {result?.is_personal_best && (
            <p className="font-medium text-positive">New personal best</p>
          )}
          {result && !result.is_personal_best && (
            <p className="text-sm text-muted">Best {result.best_score}</p>
          )}
          <div className="flex justify-center gap-2">
            <Button onClick={begin} disabled={startPlay.isPending}>
              Play again
            </Button>
            <Link to="/arcade">
              <Button variant="secondary">Back to arcade</Button>
            </Link>
          </div>
          <ErrorText error={startPlay.error} />
        </Card>
      )}
    </div>
  )
}

/** Arrow keys / WASD, shared by several games. */
export function useDirectionKeys(
  handler: (direction: 'up' | 'down' | 'left' | 'right') => void,
  active = true,
) {
  useEffect(() => {
    if (!active) return
    function onKey(event: KeyboardEvent) {
      const map: Record<string, 'up' | 'down' | 'left' | 'right'> = {
        ArrowUp: 'up',
        ArrowDown: 'down',
        ArrowLeft: 'left',
        ArrowRight: 'right',
        w: 'up',
        s: 'down',
        a: 'left',
        d: 'right',
      }
      const direction = map[event.key]
      if (direction) {
        // Otherwise the arrow keys scroll the page out from under the game.
        event.preventDefault()
        handler(direction)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [handler, active])
}

/** A requestAnimationFrame loop that ticks at a fixed interval. */
export function useGameLoop(
  tick: () => void,
  intervalMs: number,
  running: boolean,
) {
  useEffect(() => {
    if (!running) return
    let frame = 0
    let last = performance.now()

    function step(now: number) {
      // Accumulated rather than assumed: a background tab throttles rAF, and
      // resuming should not fast-forward the game by a hundred ticks.
      if (now - last >= intervalMs) {
        last = now
        tick()
      }
      frame = requestAnimationFrame(step)
    }

    frame = requestAnimationFrame(step)
    return () => cancelAnimationFrame(frame)
  }, [tick, intervalMs, running])
}

/** On-screen d-pad, so the games work on a phone. */
export function TouchPad({
  onDirection,
}: {
  onDirection: (d: 'up' | 'down' | 'left' | 'right') => void
}) {
  const button =
    'size-14 rounded-xl border border-border-subtle bg-surface text-xl active:bg-brand-soft'
  return (
    <div className="mt-4 grid grid-cols-3 justify-items-center gap-1 sm:hidden">
      <span />
      <button className={button} onClick={() => onDirection('up')} aria-label="Up">
        ↑
      </button>
      <span />
      <button className={button} onClick={() => onDirection('left')} aria-label="Left">
        ←
      </button>
      <button className={button} onClick={() => onDirection('down')} aria-label="Down">
        ↓
      </button>
      <button className={button} onClick={() => onDirection('right')} aria-label="Right">
        →
      </button>
    </div>
  )
}
